import React, { useCallback } from 'react';
import {
  createComponentAs,
  findNodePath,
  getPluginOptions,
  useElement,
  usePlateEditorRef,
  ResizeHandle,
  resizeLengthClampStatic,
  useOverrideColSize,
  useOverrideMarginLeft,
  useOverrideRowSize,
  useTableStore,
  setTableColSize,
  setTableMarginLeft,
  setTableRowSize,
  useTableColSizes,
  roundCellSizeToStep,
  ELEMENT_TABLE,
} from '@udecode/plate';

import { KEY_DOCUMENT_SETTINGS } from '../document-settings/createDocumentSettingsPlugin';

export const useTableCellElementResizableProps = ({
  colIndex,
  rowIndex,
  step,
  stepX = step,
  stepY = step,
}) => {
  const editor = usePlateEditorRef();
  const element = useElement();
  const { colSpan } = element || {};
  const tableElement = useElement(ELEMENT_TABLE);
  const { minColumnWidth = 0 } = getPluginOptions(
    editor,
    ELEMENT_TABLE,
  );

  const { innerWidth } = getPluginOptions(editor, KEY_DOCUMENT_SETTINGS);

  const [
    hoveredColIndex,
    setHoveredColIndex,
  ] = useTableStore().use.hoveredColIndex();

  const colSizesWithoutOverrides = useTableColSizes(tableElement, {
    disableOverrides: true,
  });
  // console.log('col size without override', colSizesWithoutOverrides);
  const { marginLeft = 0 } = tableElement;

  const overrideColSize = useOverrideColSize();
  const overrideRowSize = useOverrideRowSize();
  const overrideMarginLeft = useOverrideMarginLeft();

  /* eslint-disable no-shadow */
  const setColSize = useCallback(
    (colIndex, width) => {
      setTableColSize(
        editor,
        { colIndex, width },
        { at: findNodePath(editor, element) },
      );

      // Prevent flickering
      setTimeout(() => overrideColSize(colIndex, null), 0);
    },
    [editor, element, overrideColSize],
  );

  /* eslint-disable no-shadow */
  const setRowSize = useCallback(
    (rowIndex, height) => {
      setTableRowSize(
        editor,
        { rowIndex, height },
        { at: findNodePath(editor, element) },
      );

      // Prevent flickering
      setTimeout(() => overrideRowSize(rowIndex, null), 0);
    },
    [editor, element, overrideRowSize],
  );

  const setMarginLeft = useCallback(
    (marginLeft) => {
      setTableMarginLeft(
        editor,
        { marginLeft },
        { at: findNodePath(editor, element) },
      );

      // Prevent flickering
      setTimeout(() => overrideMarginLeft(null), 0);
    },
    [editor, element, overrideMarginLeft],
  );

  const handleResizeRight = useCallback(
    ({ initialSize, delta, finished }) => {
      // Magic number 20 is to account for table paddings, which this callback
      // doesn't account for. Just the inner width.
      const currentInitial = colSpan ?
        colSizesWithoutOverrides[colIndex] :
        initialSize + 20;
      const nextInitial = colSizesWithoutOverrides[colIndex + 1];

      const complement = (width) =>
        currentInitial + nextInitial - width;

      // If moving handle right of the last column in a table, calculate all widths except for last column.
      // Then ensure that new width plus the rest does not exceed document inner width.
      const maxColumnWidth = (!nextInitial && innerWidth) ?
        innerWidth - colSizesWithoutOverrides.slice(0, -1).reduce((prev, value) => prev + value, 0) - marginLeft :
        undefined;

      const currentNew = roundCellSizeToStep(
        resizeLengthClampStatic(currentInitial + delta, {
          min: minColumnWidth,
          max: nextInitial ? complement(minColumnWidth) : maxColumnWidth,
        }),
        stepX,
      );

      const nextNew = nextInitial ? complement(currentNew) : undefined;

      const fn = finished ? setColSize : overrideColSize;
      fn(colIndex, currentNew);
      if (nextNew) fn(colIndex + 1, nextNew);
    },
    [
      colIndex,
      colSizesWithoutOverrides,
      minColumnWidth,
      overrideColSize,
      setColSize,
      stepX,
    ],
  );

  const handleResizeBottom = useCallback(
    (event) => {
      const newHeight = roundCellSizeToStep(
        event.initialSize + event.delta,
        stepY,
      );

      if (event.finished) {
        setRowSize(rowIndex, newHeight);
      } else {
        overrideRowSize(rowIndex, newHeight);
      }
    },
    [overrideRowSize, rowIndex, setRowSize, stepY],
  );

  const handleResizeLeft = useCallback(
    (event) => {
      const initial = colSizesWithoutOverrides[colIndex];

      const complement = (width) => initial + marginLeft - width;

      const newMargin = roundCellSizeToStep(
        resizeLengthClampStatic(marginLeft + event.delta, {
          min: 0,
          max: complement(minColumnWidth),
        }),
        stepX,
      );

      const newWidth = complement(newMargin);

      if (event.finished) {
        setMarginLeft(newMargin);
        setColSize(colIndex, newWidth);
      } else {
        overrideMarginLeft(newMargin);
        overrideColSize(colIndex, newWidth);
      }
    },
    [
      colIndex,
      colSizesWithoutOverrides,
      marginLeft,
      minColumnWidth,
      overrideColSize,
      overrideMarginLeft,
      setColSize,
      setMarginLeft,
      stepX,
    ],
  );

  /* eslint-disable no-shadow */
  const getHandleHoverProps = (colIndex) => ({
    onHover: () => {
      if (hoveredColIndex === null) {
        setHoveredColIndex(colIndex);
      }
    },
    onHoverEnd: () => {
      if (hoveredColIndex === colIndex) {
        setHoveredColIndex(null);
      }
    },
  });

  const commonHandleProps = {
    startMargin: -12,
  };

  return {
    rightProps: {
      direction: 'right',
      onResize: handleResizeRight,
      ...getHandleHoverProps(colIndex),
      ...commonHandleProps,
    },
    bottomProps: {
      direction: 'bottom',
      onResize: handleResizeBottom,
      ...commonHandleProps,
    },
    leftProps: {
      direction: 'left',
      onResize: handleResizeLeft,
      ...getHandleHoverProps(-1),
      ...commonHandleProps,
    },
  };
};

export const PlateTableCellElementResizable = createComponentAs(
  (props) => {
    const editor = usePlateEditorRef();
    const { disableMarginLeft } = getPluginOptions(
      editor,
      ELEMENT_TABLE,
    );
    const { readOnly, colIndex } = props;
    const {
      rightProps,
      bottomProps,
      leftProps,
    } = useTableCellElementResizableProps(props);

    const hasLeftHandle = colIndex === 0 && !disableMarginLeft;

    return (
      !readOnly && (
        <>
          <ResizeHandle {...rightProps} />
          <ResizeHandle {...bottomProps} />
          {hasLeftHandle && <ResizeHandle {...leftProps} />}
        </>
      )
    );
  },
);
